import React from 'react'
import styled, { css } from 'styled-components'
import { array, object, func, bool, string, number, oneOfType } from 'prop-types'
import pick from 'lodash/pick'
import { rgba } from 'polished'

import { white, greyDark, greyLight, borderColor, red, black } from 'styles/colors'
import { inputHeight, inputHeightMobile, animationTime, animationCurve, borderRadius } from 'styles/global'
import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { fontSizes, fontWeights } from 'styles/typography'

import Icon from 'components/Icon'
import Label from 'components/Label'

import DownChevron from 'icons/down-chevron.svg'

const Container = styled.div`
  display: inline-block;
  /* min-width: 100px; */

  ${props =>
    props.full &&
    css`
      display: block;
      width: 100%;
    `};

  option {
    color: ${black} !important;
  }
`

const Wrap = styled.div`
  position: relative;
  border-radius: ${borderRadius};
  box-shadow: 0px 4px 14px rgba(0, 0, 79, 0.05);

  ${props =>
    props.isFloating &&
    css`
      box-shadow: none;
      border-radius: 0;
    `}

  ${props =>
    props.inNav &&
    css`
      box-shadow: none;
    `}
`

const StyledLabel = styled(Label)`
  position: static;

  ${props =>
    props.isFloating &&
    css`
      position: relative;
      z-index: 10;
      margin-top: -13px;
      font-weight: ${fontWeights.regular};
      font-size: inherit;
      color: ${rgba(black, 0.6)};
      margin-bottom: 0;
      top: 31px;
      pointer-events: none;
      transition: transform ${animationTime} ${animationCurve};
      transform-origin: top left;

      ${media.medium`
        top: 35px;
      `};
    `}

  ${props =>
    props.isFloating &&
    props.isFilled &&
    css`
      transform: translateY(-19px) scale(0.8);

      ${media.medium`
        transform: translateY(-23px) scale(0.8);
      `};
    `}
`
const Select = styled.select`
  display: block;
  height: ${inputHeightMobile};
  border-radius: ${borderRadius};
  background-color: ${white};
  width: 100%;
  resize: none;
  color: ${greyDark};
  vertical-align: top;
  outline: 0;
  padding: 0 ${spacing.medium};
  padding-right: 48px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px ${borderColor};
  transition: box-shadow ${animationTime} ${animationCurve};

  &[disabled] {
    color: ${greyLight};
    pointer-events: none;
    user-select: none;
    background-color: rgba(245, 247, 250, 0.75);
    cursor: default;
  }

  ${media.medium`
    height: ${inputHeight};
  `};

  ${props =>
    props.hasPlaceholder &&
    !props.isFilled &&
    css`
      color: ${rgba(black, 0.6)};
    `}

  ${props =>
    props.isFloating &&
    `
    border-radius: 0;
    color: ${props.isFilled ? black : white};
    box-shadow: inset 0 -1px 0 0 ${greyLight};
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;

    &:focus {
      box-shadow: inset 0 -1px 0 0 ${black};
    }

    &[disabled] {
      color: ${greyLight};
      pointer-events: none;
      user-select: none;
      opacity: 0.5; /* Overwrite iOS styles */
      box-shadow: inset 0 -1px 0 0 ${greyLight};
      background-color: transparent;
    }
  `}

  ${props =>
    props.inNav &&
    css`
      transition: color ${animationTime} ${animationCurve}, border ${animationTime} ${animationCurve};
      color: ${white};
      background: transparent;
      border: 0;
      box-shadow: none;
      font-weight: ${fontWeights.medium};

      ${mediaDown.medium`
        color: ${black};
      `};

      ${props =>
        !props.transparent &&
        css`
          color: ${black};
        `}
    `}
`

const Chevron = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  right: ${spacing.medium};
  transform: translateY(-50%);
  pointer-events: none;

  ${props =>
    props.isFloating &&
    `
    right: 0;
  `}

  color: currentColor;
  transition: all ${animationTime} ${animationCurve};
`

const ErrorMessage = styled.small`
  color: ${red};
  display: block;
  margin-top: ${spacing.small};
  font-size: ${fontSizes.tiny};
  text-align: left;

  ${props =>
    props.isFloating &&
    `
    margin-top: ${spacing.tiny};
  `}
`

const Dropdown = ({
  isFloating,
  placeholder,
  options,
  label,
  full,
  hideOptions,
  field,
  meta,
  form,
  inNav,
  transparent,
  disabled,
  ...rest
}) => {
  const fieldProps = field || pick(rest, ['value', 'onChange', 'onBlur', 'name'])
  const touched = meta?.touched || form?.touched[fieldProps.name]
  const error = meta?.error || form?.errors[fieldProps.name]
  const isFilled = Boolean(fieldProps.value)
  const hasPlaceholder = Boolean(placeholder)
  return (
    <Container key={fieldProps?.value} full={full} {...rest}>
      {label && (
        <StyledLabel isFloating={isFloating} isFilled={isFilled}>
          {label}
        </StyledLabel>
      )}
      <Wrap inNav={inNav} isFloating={isFloating}>
        <Select
          isFilled={isFilled}
          isFloating={isFloating}
          hasPlaceholder={hasPlaceholder}
          inNav={inNav}
          transparent={transparent}
          disabled={disabled}
          {...fieldProps}
        >
          {placeholder && (
            <option value="" disabled>
              {isFloating ? label : placeholder}
            </option>
          )}
          {options.map((option, i) => {
            return (
              <option key={i} value={option.value} disabled={hideOptions ? hideOptions[fieldProps.name] : false}>
                {option.label}
              </option>
            )
          })}
        </Select>
        <Chevron isFloating={isFloating}>
          <Icon icon={<DownChevron />} />
        </Chevron>
      </Wrap>
      {touched && error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  )
}
Dropdown.propTypes = {
  label: string,
  name: string,
  options: array.isRequired,
  onChange: func.isRequired,
  full: bool,
  hideOptions: object,
  value: oneOfType([string, number]),
  field: object,
  meta: object,
  form: object,
  placeholder: string,
  isFloating: bool,
  inNav: bool,
  transparent: bool
}

export default Dropdown
